/**
 * Builds the Delivery Location dropdown for the order, inquiry and purchase-order forms.
 *
 * The list is assembled in three bands:
 *
 *   1. the selected customer's saved delivery sites (Customer Warehouses)
 *   2. "Use customer's address" — built from the address on the customer record,
 *      offered only when it could actually become a `locations` row
 *   3. every other master location
 *
 * Band 2 is an ACTION, not a value. Its option carries a non-numeric sentinel that the
 * forms intercept in their change handler and never write into `location_id`; picking it
 * resolves to a real location id first. This is deliberate — every form submits
 * `Number(location_id) || 0`, which silently turns any string sentinel into `0`, and
 * that is exactly the bug this module is written to make impossible.
 *
 * Pure by design: no service, no i18n, no React imports, so it can be unit-reasoned and
 * shared by three forms that each cache and fetch locations differently.
 */

/** Parked in the option's `value`; intercepted by the forms, never submitted. */
export const CUSTOMER_ADDRESS_OPTION_VALUE = '__customer_address__';

/** The single source of truth for "this is the action option, not a real location". */
export const isSyntheticLocationValue = (value) =>
  value === CUSTOMER_ADDRESS_OPTION_VALUE;

/** Normalised key for comparing two addresses that were typed by different people. */
const addressKey = (text, city, state) =>
  [text, city, state]
    .map((part) => String(part || '').trim().toLowerCase())
    .join('|');

/**
 * The customer's own address, and whether it can become a `locations` row.
 *
 * `locations.state`, `.city` and `.delivery_location` are all NOT NULL, and `state`
 * additionally drives GST place-of-supply in the Tally export — so a missing part is
 * never guessed or defaulted. When anything required is absent the option is simply not
 * offered rather than shown broken.
 *
 * Accepts both the snake_case customer record and the new-customer form's own field
 * names (`address_line1`), because the same draft is built in both modes.
 */
export const buildCustomerAddressDraft = (party) => {
  if (!party) return null;

  const state = String(party.state || '').trim();
  const city = String(party.city || '').trim();
  const country = String(party.country || 'India').trim();

  const text =
    String(party.delivery_location || '').trim() ||
    [
      party.address_1 || party.address_line1 || party.address1,
      party.address_2 || party.address_line2 || party.address2,
    ]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');

  const summary = [text, city, state].filter(Boolean).join(', ');

  // `locations` requires state, city and delivery_location. Most imported customers have
  // an address and a state but no city, so a missing part is COLLECTED, not guessed —
  // the option is still offered and the form prompts for what is absent.
  const missingFields = [];
  if (!state) missingFields.push('state');
  if (!city) missingFields.push('city');
  if (!text) missingFields.push('address');

  return {
    // Ready to save as-is.
    materializable: missingFields.length === 0,
    // Worth offering at all. The address line is the part that makes it "the customer's
    // address"; state and city can be filled in, an absent address cannot.
    offerable: Boolean(text),
    missingFields,
    delivery_location: text,
    city,
    state,
    country,
    zip_code: String(party.zip_code || party.zipCode || party.pincode || '').trim(),
    summary,
    key: addressKey(text, city, state),
  };
};

/** Label for one saved/offline warehouse row, preserving each mode's own fallbacks. */
const warehouseLabel = (wh, shape) => {
  if (shape === 'offline') return wh.address || wh.city || wh.name;
  return wh.location?.delivery_location || wh.location?.name || wh.name;
};

/** The address a warehouse points at, for de-duping against the customer's own address. */
const warehouseKey = (wh, shape) =>
  shape === 'offline'
    ? addressKey(wh.address, wh.city, wh.state)
    : addressKey(wh.location?.delivery_location, wh.location?.city, wh.location?.state);

/**
 * @param {object}        args
 * @param {Array}         args.warehouses      customer.warehouses[] or the offline drafts
 * @param {'saved'|'offline'} args.warehouseShape
 * @param {Array}         args.locations       master locations currently in form state
 * @param {string|number} args.selectedId      the form's current location id
 * @param {object|null}   args.addressDraft    from buildCustomerAddressDraft
 * @param {object}        args.labels          { warehouse, default, useCustomerAddress }
 * @param {function}      [args.locationLabel] per-form fallback for a master location
 * @returns {Array<{value, label, className?}>}
 */
export const buildDeliveryLocationOptions = ({
  warehouses = [],
  warehouseShape = 'saved',
  locations = [],
  selectedId = '',
  addressDraft = null,
  labels = {},
  locationLabel,
} = {}) => {
  const options = [];
  const seenIds = new Set();
  const seenAddresses = new Set();

  // 1. The customer's saved sites, first and unfiltered — a server-side search on the
  //    master list must never push them out of view.
  (Array.isArray(warehouses) ? warehouses : []).forEach((wh) => {
    if (!wh || !wh.location_id) return;
    const id = String(wh.location_id);
    if (seenIds.has(id)) return;
    seenIds.add(id);
    seenAddresses.add(warehouseKey(wh, warehouseShape));
    options.push({
      value: wh.location_id,
      label: `${warehouseLabel(wh, warehouseShape)} ${labels.warehouse || '(Warehouse)'}${
        wh.is_default ? ` ${labels.default || '[Default]'}` : ''
      }`,
    });
  });

  // 2. The customer's own address — suppressed once a saved site already covers it, so
  //    the same place is never offered twice. Offered even when incomplete: picking it
  //    then opens a prefilled form asking for the missing part, which is the normal case
  //    since imported customers have an address and a state but no city.
  if (addressDraft && addressDraft.offerable && !seenAddresses.has(addressDraft.key)) {
    const base = labels.useCustomerAddress || "Use customer's address";
    const needs =
      !addressDraft.materializable && labels.needsDetails
        ? ` (${labels.needsDetails})`
        : '';
    options.push({
      value: CUSTOMER_ADDRESS_OPTION_VALUE,
      label: `${base} — ${addressDraft.summary}${needs}`,
    });
  }

  // 3. Everything else. The first master row carries a divider so the customer's own
  //    entries read as a group — SearchableSelect has no real section headings, and a
  //    fake one would be clickable and fire onChange with a junk value.
  const fallback = locationLabel || ((l) => l.delivery_location || l.city || l.name);
  let firstMaster = true;
  (Array.isArray(locations) ? locations : []).forEach((l) => {
    if (!l || l.id === undefined || l.id === null) return;
    const id = String(l.id);
    if (seenIds.has(id)) return;
    seenIds.add(id);
    options.push({
      value: l.id,
      label: fallback(l),
      ...(firstMaster && options.length > 0 ? { className: 'border-t' } : {}),
    });
    firstMaster = false;
  });

  // 4. Keep the current selection visible. A debounced search replaces `locations`
  //    wholesale with 20 server rows, which would otherwise blank the field mid-edit.
  if (selectedId && !isSyntheticLocationValue(selectedId) && !seenIds.has(String(selectedId))) {
    const match = (Array.isArray(locations) ? locations : []).find(
      (l) => String(l.id) === String(selectedId)
    );
    if (match) {
      options.push({ value: match.id, label: fallback(match) });
    }
  }

  return options;
};
